import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import {
    AppBar, Toolbar, Typography, Button, IconButton,
    Box, CircularProgress, TextField, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
    Select, MenuItem
} from '@material-ui/core'
import ExitToAppIcon from '@material-ui/icons/ExitToApp'
import BuildIcon from '@material-ui/icons/Build'
import DescriptionIcon from '@material-ui/icons/Description'
import AddIcon from '@material-ui/icons/Add'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import AssignmentIcon from '@material-ui/icons/Assignment'
import GetAppIcon from '@material-ui/icons/GetApp'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'

import { subscribeTasksByDni, getInvoicesByDni, logoutPortal, computeNumInvoice, getInvoiceDetail } from './portalApi'
import logo from 'common/assets/ifeno_logo_blanco.png'
import fondoPortal from 'common/assets/logo_fondo_portal_vectorized.png'
import addDeviceFromPortalUseCase from './addDeviceFromPortalUseCase'
import PortalTaskCard from './PortalTaskCard'
import PortalInvoiceModal from './PortalInvoiceModal'
import { dark } from './portalStyles'
import { translations } from './portalI18n'

const VIEWS = { repairs: 'repairs', invoices: 'invoices' }
const IS_MOBILE = window.innerWidth <= 600

const initialForm = { deviceName: '', description: '', imei: '', refClient: '', observations: '', unlock: '', shipping: true }

const NAV_ITEMS = [
    { view: VIEWS.repairs, labelKey: 'repairs', Icon: BuildIcon },
    { view: VIEWS.invoices, labelKey: 'invoices', Icon: DescriptionIcon },
]

// CSS override para campos MUI en modo oscuro
const darkFieldCSS = `
  .portal-dark-field .MuiOutlinedInput-notchedOutline { border-color: #2e2e2e !important; }
  .portal-dark-field .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline { border-color: #555 !important; }
  .portal-dark-field .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline { border-color: #888 !important; }
  .portal-dark-field .MuiInputLabel-outlined { color: #666 !important; }
  .portal-dark-field .MuiInputLabel-outlined.Mui-focused { color: #aaa !important; }
  .portal-dark-field .MuiSelect-root { color: #e8e8e8 !important; background: #1a1a1a; }
  .portal-dark-field .MuiSelect-icon { color: #666 !important; }
  .portal-dark-menu .MuiPaper-root { background: #1a1a1a !important; border: 1px solid #2e2e2e; }
  .portal-dark-menu .MuiMenuItem-root { color: #e8e8e8 !important; }
  .portal-dark-menu .MuiMenuItem-root:hover { background: #242424 !important; }
  .portal-dark-menu .MuiListItem-root.Mui-selected { background: #2e2e2e !important; }
`

const PortalDashboard = ({ client, lang = 'es', setLang }) => {
    const t = translations[lang]
    const [tasks, setTasks] = useState([])
    const [invoices, setInvoices] = useState([])
    const [taskInvoiceMap, setTaskInvoiceMap] = useState({})
    const [loading, setLoading] = useState(true)
    const [view, setView] = useState(VIEWS.repairs)
    const [selectedInvoice, setSelectedInvoice] = useState(null)
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const [showPageTooltip, setShowPageTooltip] = useState(false)
    const [invPage, setInvPage] = useState(0)
    const [invPageSize, setInvPageSize] = useState(10)
    const [showInvPageTooltip, setShowInvPageTooltip] = useState(false)
    const [search, setSearch] = useState('')

    const invoiceDetailCache = useRef({})

    const handleOpenInvoice = useCallback(async (inv) => {
        if (!invoiceDetailCache.current[inv.id]) {
            invoiceDetailCache.current[inv.id] = await getInvoiceDetail(inv.id)
        }
        setSelectedInvoice({ ...inv, _detail: invoiceDetailCache.current[inv.id] })
    }, [])

    const [showNewForm, setShowNewForm] = useState(false)
    const [form, setForm] = useState(initialForm)
    const [saving, setSaving] = useState(false)
    const [formError, setFormError] = useState('')
    const [formSuccess, setFormSuccess] = useState(false)


    useEffect(() => {
        // Snapshot síncrono — se registra inmediatamente, cleanup siempre lo cancela
        const unsubscribe = subscribeTasksByDni(client.id, (fetchedTasks) => {
            setTasks(fetchedTasks)
            setLoading(false)
        })

        // Facturas en paralelo, independiente del snapshot
        const loadInvoices = async () => {
            try {
                const rawInvoices = await getInvoicesByDni(client.id)
                const i = rawInvoices.map(inv => ({ ...inv, numInvoice: computeNumInvoice(inv, rawInvoices) }))
                const map = {}
                i.forEach(inv => { if (inv.taskReference) map[inv.taskReference] = inv })
                setInvoices(i)
                setTaskInvoiceMap(map)

                if (i.length > 0) {
                    const results = await Promise.allSettled(i.map(inv => getInvoiceDetail(inv.id)))
                    const fullMap = { ...map }
                    i.forEach((inv, idx) => {
                        const r = results[idx]
                        if (r.status === 'fulfilled') {
                            invoiceDetailCache.current[inv.id] = r.value
                            if (r.value?.data) {
                                r.value.data.forEach(item => {
                                    if (item.reference) fullMap[item.reference] = inv
                                })
                            }
                        }
                    })
                    setTaskInvoiceMap(fullMap)
                }
            } catch (err) {
                console.error('Error al cargar facturas', err)
            }
        }

        loadInvoices()
        return () => unsubscribe()
    }, [client.id])

    const handleField = (field) => (e) => {
        const raw = e.target.value
        const value = (field === 'deviceName' || field === 'description') ? raw.toUpperCase() : raw
        setForm(p => ({ ...p, [field]: value }))
    }

    const handleSubmitDevice = async () => {
        if (!form.deviceName.trim() || !form.description.trim()) {
            setFormError(t.errorRequired)
            return
        }
        setFormError('')
        setSaving(true)
        try {
            await addDeviceFromPortalUseCase(client, form)
            setForm(initialForm)
            setFormSuccess(true)
            setTimeout(() => {
                setFormSuccess(false)
                setShowNewForm(false)
            }, 1500)
        } catch (err) {
            console.error(err)
            setFormError(t.errorSaving)
        } finally {
            setSaving(false)
        }
    }

    const handleCancelForm = () => {
        setShowNewForm(false)
        setForm(initialForm)
        setFormError('')
        setFormSuccess(false)
    }

    const handleChangeView = (v) => {
        setView(v)
        setSelectedInvoice(null)
        setPage(0)
        setInvPage(0)
        if (v !== VIEWS.repairs) handleCancelForm()
    }

    const searchTerm = search.trim().toLowerCase()

    const sortedTasks = useMemo(() => {
        const filtered = searchTerm
            ? tasks.filter(task =>
                [task.reference, task.refClient, task.imei, task.deviceName, task.description]
                    .some(v => (v || '').toLowerCase().includes(searchTerm))
              )
            : tasks
        return [...filtered].sort((a, b) => (b.dateEntry || 0) - (a.dateEntry || 0))
    }, [tasks, searchTerm])

    const totalPages = Math.ceil(sortedTasks.length / pageSize)
    const visibleTasks = useMemo(
        () => sortedTasks.slice(page * pageSize, (page + 1) * pageSize),
        [sortedTasks, page, pageSize]
    )
    const totalPending = useMemo(
        () => sortedTasks.filter(task => ![2, 4].includes(Number(task.state))).length,
        [sortedTasks]
    )

    const invTotalPages = Math.ceil(invoices.length / invPageSize)
    const visibleInvoices = useMemo(
        () => invoices.slice(invPage * invPageSize, (invPage + 1) * invPageSize),
        [invoices, invPage, invPageSize]
    )

    const repairsPagination = () => sortedTasks.length > 5 && (
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Tooltip title={t.itemsPerPage} placement="top" open={showPageTooltip} disableFocusListener disableHoverListener disableTouchListener>
                <Select
                    value={pageSize}
                    onChange={e => { setPageSize(e.target.value); setPage(0) }}
                    onOpen={e => e.currentTarget.blur()}
                    onMouseEnter={() => { setShowPageTooltip(true); setTimeout(() => setShowPageTooltip(false), 2000) }}
                    onMouseLeave={() => setShowPageTooltip(false)}
                    disableUnderline className="portal-dark-field"
                    style={{ fontSize: 12, color: dark.text, border: `1px solid ${dark.border}`, borderRadius: 6, padding: '2px 6px', height: 28, background: dark.surface }}
                    MenuProps={{ anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, getContentAnchorEl: null, PopoverClasses: { paper: 'portal-dark-menu' }, disableScrollLock: true }}
                >
                    {[5, 10, 25, 50, 100].map(n => <MenuItem key={n} value={n} style={{ fontSize: 12, color: dark.text, background: dark.surface }}>{n}</MenuItem>)}
                </Select>
            </Tooltip>
            {totalPages > 1 && (
                <Box display="flex" alignItems="center" style={{ background: dark.surface, borderRadius: 8, padding: '4px 6px', gap: 4 }}>
                    <IconButton size="small" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ padding: 4, color: page === 0 ? dark.text3 : dark.text }}>
                        <ChevronLeftIcon style={{ fontSize: 18 }} />
                    </IconButton>
                    <Typography variant="caption" style={{ color: dark.text2, minWidth: 40, textAlign: 'center' }}>{page + 1} / {totalPages}</Typography>
                    <IconButton size="small" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page === totalPages - 1} style={{ padding: 4, color: page === totalPages - 1 ? dark.text3 : dark.text }}>
                        <ChevronRightIcon style={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            )}
        </Box>
    )

    const invoicesPagination = (invTotalPages) => invoices.length > 5 && (
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Tooltip title={t.itemsPerPage} placement="top" open={showInvPageTooltip} disableFocusListener disableHoverListener disableTouchListener>
                <Select
                    value={invPageSize}
                    onChange={e => { setInvPageSize(e.target.value); setInvPage(0) }}
                    onOpen={e => e.currentTarget.blur()}
                    onMouseEnter={() => { setShowInvPageTooltip(true); setTimeout(() => setShowInvPageTooltip(false), 2000) }}
                    onMouseLeave={() => setShowInvPageTooltip(false)}
                    disableUnderline className="portal-dark-field"
                    style={{ fontSize: 12, color: dark.text, border: `1px solid ${dark.border}`, borderRadius: 6, padding: '2px 6px', height: 28, background: dark.surface }}
                    MenuProps={{ anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, getContentAnchorEl: null, PopoverClasses: { paper: 'portal-dark-menu' }, disableScrollLock: true }}
                >
                    {[5, 10, 25, 50, 100].map(n => <MenuItem key={n} value={n} style={{ fontSize: 12, color: dark.text, background: dark.surface }}>{n}</MenuItem>)}
                </Select>
            </Tooltip>
            {invTotalPages > 1 && (
                <Box display="flex" alignItems="center" style={{ background: dark.surface, borderRadius: 8, padding: '4px 6px', gap: 4 }}>
                    <IconButton size="small" onClick={() => setInvPage(p => Math.max(0, p - 1))} disabled={invPage === 0} style={{ padding: 4, color: invPage === 0 ? dark.text3 : dark.text }}>
                        <ChevronLeftIcon style={{ fontSize: 18 }} />
                    </IconButton>
                    <Typography variant="caption" style={{ color: dark.text2, minWidth: 40, textAlign: 'center' }}>{invPage + 1} / {invTotalPages}</Typography>
                    <IconButton size="small" onClick={() => setInvPage(p => Math.min(invTotalPages - 1, p + 1))} disabled={invPage === invTotalPages - 1} style={{ padding: 4, color: invPage === invTotalPages - 1 ? dark.text3 : dark.text }}>
                        <ChevronRightIcon style={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            )}
        </Box>
    )

    const fieldProps = (field, extraProps = {}) => ({
        fullWidth: true,
        variant: 'outlined',
        size: 'small',
        value: form[field],
        onChange: handleField(field),
        disabled: saving,
        className: 'portal-dark-field',
        InputLabelProps: { style: { color: '#666' } },
        InputProps: { style: { color: dark.text, background: dark.surface2 } },
        ...extraProps,
    })

    return (
        <Box minHeight="100vh" style={{ background: dark.bg }}>
            <img src={fondoPortal} alt="" style={IS_MOBILE
                ? { position: 'fixed', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', zIndex: 0, opacity: 0.5, pointerEvents: 'none', userSelect: 'none' }
                : { position: 'fixed', left: -20, top: '50%', transform: 'translateY(-50%)', height: '115vh', width: 'auto', zIndex: 0, opacity: 0.5, pointerEvents: 'none', userSelect: 'none' }
            } />
            <div style={{ position: 'relative', zIndex: 1 }}>
            <style>{darkFieldCSS}</style>

            <AppBar
                position="sticky"
                elevation={0}
                style={{ background: '#0f0f0f', borderBottom: `1px solid ${dark.border}`, height: 52 }}
            >
                <Toolbar
                    disableGutters
                    style={{ minHeight: 52, height: 52, padding: '0 16px', overflow: 'hidden' }}
                >
                    <Box style={{ flex: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                        <img src={logo} alt="logo" style={{ height: 26, maxWidth: 110, objectFit: 'contain', display: 'block' }} />
                        <Typography style={{ fontSize: 9, color: dark.text2, letterSpacing: '0.8px', textTransform: 'uppercase', lineHeight: 1 }}>
                            Portal de cliente
                        </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                        {NAV_ITEMS.map(({ view: v, labelKey, Icon }) => (
                            <Tooltip key={v} title={t[labelKey]} placement="bottom">
                                <IconButton
                                    onClick={() => handleChangeView(v)}
                                    size="small"
                                    style={{
                                        color: view === v ? '#f0f0f0' : dark.text3,
                                        background: view === v ? 'rgba(255,255,255,0.1)' : 'transparent',
                                        borderRadius: 8, padding: 8,
                                        transition: 'color 0.15s, background 0.15s',
                                    }}
                                >
                                    <Icon style={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        ))}
                    </Box>

                    <Box style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                        <Tooltip title={t.switchLang} placement="bottom">
                            <button
                                onClick={() => setLang(lang === 'es' ? 'en' : 'es')}
                                style={{
                                    background: 'transparent', border: `1px solid ${dark.border}`,
                                    borderRadius: 6, padding: '2px 8px',
                                    color: dark.text2, fontSize: 11, fontWeight: 700,
                                    cursor: 'pointer', fontFamily: 'inherit',
                                }}
                            >
                                {lang === 'es' ? 'EN' : 'ES'}
                            </button>
                        </Tooltip>
                        <Tooltip title={t.signOut} placement="bottom">
                            <IconButton
                                onClick={logoutPortal}
                                size="small"
                                style={{ color: '#c00', padding: 8, borderRadius: 8, transition: 'color 0.15s, background 0.15s' }}
                                onMouseEnter={e => { e.currentTarget.style.color = '#f0f0f0'; e.currentTarget.style.background = '#c00' }}
                                onMouseLeave={e => { e.currentTarget.style.color = '#c00'; e.currentTarget.style.background = 'transparent' }}
                            >
                                <ExitToAppIcon style={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Toolbar>
            </AppBar>

            <Box key={view} maxWidth={680} mx="auto" px={2} py={3}>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={10}>
                        <CircularProgress style={{ color: dark.text2 }} />
                    </Box>

                ) : view === VIEWS.repairs ? (
                    <>
                        {/* Cabecera con botón añadir, búsqueda y paginación */}
                        <Box display="flex" alignItems="center" mb={1.5} style={{ gap: 8 }}>
                            <Box style={{ background: dark.surface, borderRadius: 8, padding: '4px 6px', flexShrink: 0 }}>
                                <Tooltip title={t.newRepair}>
                                    <IconButton
                                        size="small"
                                        onClick={() => setShowNewForm(v => !v)}
                                        style={{
                                            borderRadius: 8, padding: '5px 10px',
                                            background: showNewForm ? '#f0f0f0' : 'transparent',
                                            color: showNewForm ? '#111' : dark.text,
                                            display: 'flex', gap: 2,
                                        }}
                                    >
                                        <AddIcon style={{ fontSize: 16 }} />
                                        <BuildIcon style={{ fontSize: 15 }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Box flex={1} style={{ position: 'relative' }}>
                                <SearchIcon style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: dark.text2, pointerEvents: 'none' }} />
                                <input
                                    value={search}
                                    onChange={e => { setSearch(e.target.value); setPage(0) }}
                                    placeholder={IS_MOBILE ? "" : t.searchPlaceholder}
                                    style={{
                                        width: '100%', boxSizing: 'border-box',
                                        background: dark.surface, border: `1px solid ${dark.border}`,
                                        borderRadius: 8, padding: '6px 32px 6px 28px',
                                        color: dark.text, fontSize: 16, fontFamily: 'inherit', outline: 'none',
                                    }}
                                />
                                {search && (
                                    <IconButton size="small" onClick={() => { setSearch(''); setPage(0) }}
                                        style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', padding: 2, color: dark.text2 }}>
                                        <ClearIcon style={{ fontSize: 14 }} />
                                    </IconButton>
                                )}
                            </Box>
                            <Box flexShrink={0}>{repairsPagination()}</Box>
                        </Box>

                        {/* Modal nueva reparación */}
                        <Dialog
                            open={showNewForm}
                            onClose={handleCancelForm}
                            fullWidth
                            maxWidth="sm"
                            PaperProps={{ style: { background: dark.surface, border: `1px solid ${dark.border}`, borderRadius: 12 } }}
                        >
                            <DialogTitle style={{ paddingBottom: 8, textAlign: 'center' }}>
                                <Typography style={{ fontWeight: 700, fontSize: 16, color: dark.text }}>{t.formTitle}</Typography>
                            </DialogTitle>
                            <DialogContent>
                                {formSuccess ? (
                                    <Box textAlign="center" py={4}>
                                        <CheckCircleOutlineIcon style={{ fontSize: 48, color: '#4cc488', marginBottom: 8 }} />
                                        <Typography variant="body1" style={{ fontWeight: 600, color: dark.text }}>
                                            {t.repairRegistered}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <>
                                        <Box mb={1.5} mt={0.5}>
                                            <TextField
                                                label={t.fieldDevice}
                                                placeholder={t.fieldDevicePh}
                                                {...fieldProps('deviceName')}
                                            />
                                        </Box>
                                        <Box mb={1.5}>
                                            <TextField
                                                label={t.fieldFault}
                                                placeholder={t.fieldFaultPh}
                                                {...fieldProps('description')}
                                            />
                                        </Box>
                                        <Box mb={1.5}>
                                            <TextField
                                                label={t.fieldImei}
                                                placeholder={t.fieldOptional}
                                                inputProps={{ maxLength: 20 }}
                                                {...fieldProps('imei')}
                                            />
                                        </Box>
                                        <Box mb={1.5}>
                                            <TextField
                                                label={t.fieldUnlock}
                                                placeholder={t.fieldOptional}
                                                {...fieldProps('unlock')}
                                            />
                                        </Box>
                                        <Box mb={1.5}>
                                            <TextField
                                                label={t.fieldRefClient}
                                                placeholder={t.fieldOptional}
                                                {...fieldProps('refClient')}
                                            />
                                        </Box>
                                        <Box mb={0.5}>
                                            <TextField
                                                label={t.fieldNotes}
                                                placeholder={t.fieldNotesPh}
                                                multiline
                                                minRows={3}
                                                inputProps={{ style: { resize: 'vertical', color: dark.text } }}
                                                {...fieldProps('observations')}
                                            />
                                        </Box>
                                        {formError && (
                                            <Typography variant="caption" style={{ color: '#e05555', display: 'block', marginTop: 8 }}>
                                                {formError}
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </DialogContent>
                            {!formSuccess && (
                                <DialogActions style={{ padding: '12px 24px', justifyContent: 'center' }}>
                                    <Button
                                        onClick={handleCancelForm}
                                        disabled={saving}
                                        style={{ textTransform: 'none', color: dark.text2 }}
                                    >
                                        {t.cancel}
                                    </Button>
                                    <Button
                                        onClick={handleSubmitDevice}
                                        variant="contained"
                                        disabled={saving}
                                        style={{
                                            textTransform: 'none', fontWeight: 600, minWidth: 140,
                                            background: saving ? '#333' : '#f0f0f0', color: '#111', borderRadius: 6,
                                        }}
                                    >
                                        {saving ? t.registering : t.register}
                                    </Button>
                                </DialogActions>
                            )}
                        </Dialog>

                        {tasks.length === 0 ? (
                            <Box textAlign="center" py={8}>
                                <AssignmentIcon style={{ fontSize: 48, color: dark.text3, marginBottom: 12 }} />
                                <Typography variant="body1" style={{ color: dark.text2 }} gutterBottom>
                                    {t.noRepairs}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    onClick={() => setShowNewForm(true)}
                                    style={{ textTransform: 'none', fontWeight: 600, marginTop: 8, borderRadius: 8, color: dark.text, borderColor: dark.border }}
                                >
                                    {t.addFirstRepair}
                                </Button>
                            </Box>
                        ) : (
                            <>
                                <Box display="inline-block" style={{ background: dark.surface, borderRadius: 6, padding: '2px 10px', marginBottom: 6 }}>
                                    <Typography variant="overline" style={{ color: dark.text, fontWeight: 700, letterSpacing: 1 }}>
                                        {t.inProgress}
                                    </Typography>
                                </Box>
                                <Box>
                                    {visibleTasks.map(task => (
                                        <PortalTaskCard key={task.id} task={task} lang={lang} client={client} taskInvoiceMap={taskInvoiceMap} onOpenInvoice={handleOpenInvoice} />
                                    ))}
                                </Box>
                                <Box display="flex" justifyContent="flex-end" mt={1.5}>
                                    {repairsPagination()}
                                </Box>
                            </>
                        )}
                    </>

                ) : (
                    <>
                        {invoices.length === 0 ? (
                                <Box textAlign="center" py={8}>
                                    <DescriptionIcon style={{ fontSize: 48, color: dark.text3, marginBottom: 12 }} />
                                    <Typography variant="body1" style={{ color: dark.text2 }}>
                                        {t.noInvoices}
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    {/* Cabecera paginación facturas */}
                                    <Box display="flex" justifyContent="flex-end" alignItems="center" mb={1.5}>
                                        {invoicesPagination(invTotalPages)}
                                    </Box>

                                    {visibleInvoices.map(inv => (
                                        <Box
                                            key={inv.id}
                                            style={{
                                                background: dark.surface, borderRadius: 10,
                                                border: `1px solid ${dark.border}`, padding: '14px 18px',
                                                marginBottom: 8,
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            }}
                                        >
                                            <Box>
                                                <Typography style={{ fontWeight: 600, color: dark.text, fontSize: 13 }}>
                                                    {inv.type === 'Proforma' ? t.proforma : t.invoice} #{inv.numInvoice}
                                                </Typography>
                                                <Typography style={{ fontSize: 11, color: dark.text3, marginTop: 2 }}>
                                                    {new Date(inv.invoiceDate).toLocaleDateString(lang === 'en' ? 'en-GB' : 'es-ES')}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                                                <Typography style={{ fontWeight: 700, color: dark.text, fontSize: 14 }}>
                                                    {Number(inv.totalPrice || 0).toFixed(2)} €
                                                </Typography>
                                                <Tooltip title={t.downloadInvoice}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenInvoice(inv)}
                                                        style={{ color: dark.text, border: `1px solid ${dark.border}`, borderRadius: 6, padding: 6 }}
                                                    >
                                                        <GetAppIcon style={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    ))}
                                    <Box display="flex" justifyContent="flex-end" mt={1.5}>
                                        {invoicesPagination(invTotalPages)}
                                    </Box>
                                </>
                            )}
                    </>
                )}
            </Box>

            {selectedInvoice && (
                <PortalInvoiceModal
                    invoice={selectedInvoice}
                    lang={lang}
                    onClose={() => setSelectedInvoice(null)}
                />
            )}
            </div>
        </Box>
    )
}

export default PortalDashboard
